import API from './API';
import Conference from './Conference';
import Notification from './Notification';
import { writable } from 'svelte/store';

let content = { token: false, device: false, call: false, error: false, audioDevices: { input: [], output: [] }, currentDevice: { input: false, output: false } };
try {
	let json = window.localStorage.getItem('audioDevices');
	if (json) {
		json = JSON.parse(json);
		content.currentDevice = Object.assign(content.currentDevice, json);
	}
}
catch (error) { console.error(error); }

let twilio = writable(content);

twilio.subscribe(state => {
	try {
		const json = JSON.stringify(state.currentDevice);
		window.localStorage.setItem('audioDevices', json);
	}
	catch (error) { }
});


twilio.getToken = async () => {
	const response = await API.request('get', 'twilio/token');
	return response?.jwt ? response.jwt : false;
}

twilio.setToken = async () => {
	return new Promise(async (resolve, reject) => {
		try {
			console.log('Fetching token');
			const token = await twilio.getToken();
			if (token && content.device)
				await content.device.updateToken(token);

			console.log('Token updated');
			setTimeout(() => { resolve() }, 800);
		} catch (error) {
			reject(error);
		}
	});
};


twilio.init = async (force = false) => {
	try {
		let device = content?.device;
		if (!content.device || force) {
			const token = await twilio.getToken();
			if (token) {
				device = new Twilio.Device(token, {
					codecPreferences: ['opus', 'pcmu'],
					closeProtection: true,
					tokenRefreshMs: 30000,
				});

				await device.register();
			}
		}

		if (device) {
			twilio.setEvents(device);
			content.device = device;
			twilio.set(content);

			await twilio.audioDevices();
		}
	} catch (error) {
		console.error(error);
	}
}

// Methods

twilio.connect = async (to, conference) => {
	try {
		const device = content.device;
		let params = {};
		if (to) params.toPerson = to;
		if (conference) params.conferenceName = conference;

		if (device.state != 'registered' || !device.home && !content.error)
			await device.register();

		if (content.error)
			await twilio.init(true);
		else
			await twilio.setToken();

		if (device) {
			if (content.currentDevice.input)
				await twilio.setInputDevice(content.currentDevice.input, true, true);

			if (content.currentDevice.output)
				await twilio.setOutputDevice(content.currentDevice.output, true);

			const call = await device.connect({ params });
			content.call = call;
			twilio.set(content);
			twilio.setCallEvents();
		}
	} catch (error) {
		console.error(error);
	}
}

twilio.dial = (dial) => {
	console.log(content.call);
	content.call.sendDigits(dial);
}

twilio.callNumber = async (to) => {
	try {
		await twilio.connect(to);
	} catch (error) {
		console.error(error);
	}
}

twilio.disconnect = async () => {
	try {
		if (content.device)
			await content.device.disconnectAll();
	} catch (error) {
		console.error(error);
	}
}

twilio.audioDevices = async () => {
	try {
		let currentAudioDevices = localStorage.getItem('audioDevices');
		if (currentAudioDevices) currentAudioDevices = JSON.parse(currentAudioDevices);
		const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

		setTimeout(async () => {
			const formattedInputs = [];
			const formattedOutputs = [];
			const input = content.device.audio.availableInputDevices;
			const output = content.device.audio.availableOutputDevices;
			for (const [id, item] of input)
				formattedInputs.push({ id, name: item.label });

			for (const [id, item] of output)
				formattedOutputs.push({ id, name: item.label });

			content.audioDevices.input = formattedInputs;
			content.audioDevices.output = formattedOutputs;

			if (currentAudioDevices.input) {
				const isInputDeviceAvailable = formattedInputs.some(device => device.id === currentAudioDevices.input);
				if (isInputDeviceAvailable)
					await twilio.setInputDevice(currentAudioDevices.input);
			}

			if (currentAudioDevices.output) {
				const isOutputDeviceAvailable = formattedOutputs.some(device => device.id === currentAudioDevices.output);
				if (isOutputDeviceAvailable)
					await twilio.setOutputDevice(currentAudioDevices.output);
			}

			stream.getTracks().forEach(track => track.stop());
			twilio.set(content);
		}, 1000);
	} catch (error) {
		console.error(error);
	}
}

twilio.setInputDevice = async (id, setTwilio = false, force = false) => {
	try {
		if (setTwilio) {
			await content.device.audio.setInputDevice(id);
			if (!Conference.getActive() && !force)
				await content.device.audio.unsetInputDevice();
		}
		content.currentDevice.input = id;
		twilio.set(content);
	} catch (error) {
		console.error('Error while selecting input device', error)
	}
}

twilio.setOutputDevice = async (id, setTwilio = false) => {
	try {
		if (setTwilio)
			await content.device.audio.speakerDevices.set(id);

		content.currentDevice.output = id;

		twilio.set(content);
	} catch (error) {
		console.error('Error while selecting output device', error)
	}
}

twilio.setTestAudio = async () => {
	try {
		const device = content.device;
		await twilio.setOutputDevice(content.currentDevice.output, true);
		await device.audio.speakerDevices.test();
	} catch (error) {
		console.error(error);
	}
}

twilio.setError = (state) => {
	content.error = state;
	twilio.set(content);
}

// EVENTS

twilio.setEvents = (device) => {
	device.on('registered', function (device) {
		console.log('Device is ready');
	});
	device.on('registering', device => {
		console.log('Device is registering');
	});

	device.on('incoming', call => {
		console.log('incoming!');
		content.call = call;
		twilio.setCallEvents();
		twilio.set(content);
	});

	device.on('error', function (error) {
		console.error(error?.code, error);
		Conference.setActive(false);
		if (error?.code == 53000) {
			twilio.setError(true);
		}

		if (error?.code == 20104) {
			twilio.setToken();
		}
	});

	device.on('tokenWillExpire', async () => {
		const checkConnection = () => {
			return navigator.onLine;
		};

		const retryUpdateToken = async () => {
			const minutes = 5;
			const retryDelay = minutes * 60 * 1000;

			try {
				if (!checkConnection()) {
					console.warn('No internet connection detected, retrying in', minutes, 'minutes');
					setTimeout(() => retryUpdateToken(), retryDelay);
					return;
				}

				await twilio.setToken();
			} catch (err) {
				console.warn('Token update failed, retrying in', minutes, 'minutes');
				setTimeout(() => retryUpdateToken(), retryDelay);
			}
		};

		retryUpdateToken();
	});

	device.audio.on('deviceChange', twilio.audioDevices);
}

twilio.setCallEvents = () => {
	if (content.call) {
		content.call.on('accept', call => {
			console.log('The call was accepted.');
			twilio.setError(false);
		});

		content.call.on('cancel', call => {
			Notification.removeAgent();
		});

		content.call.on('error', (error, call) => {
			console.error(error?.code, error);
			if (error?.code == 53000) {
				twilio.setError(true);
			}
		});

		content.call.on('tokenWillExpire', () => {
			twilio.setToken();
		});

		content.call.on('disconnect', call => {
			console.log('disconnect');
			Conference.setActive(false);
			content.call = false;
			twilio.set(content);
			content.device.audio.unsetInputDevice();
			// content.device.destroy();
		});
	}
}

export default twilio;